import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Save, Phone, BarChart3, Clock, RefreshCw, PhoneForwarded, Link2, AlertTriangle, User, Mail, MessageSquare, Briefcase, ArrowRight, PhoneIncoming, PhoneOutgoing, BookOpen, Trash2, Upload, ChevronDown, ChevronRight, Shield, Search, PhoneOff, Wrench, FileText, Globe, Calendar } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet'
import { Switch } from '@/components/ui/switch'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { VariablePicker } from './VariablePicker'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip'
import { useToast } from '@/hooks/use-toast'
import api from '@/lib/api'
import { formatPhoneDisplay, formatPhoneInput } from '@/lib/utils'
import { resolveFirmShortName, buildLiveGreeting } from '@/lib/firmNameUtils'
import type { Assistant } from './AgentCard'
import { ScheduleEditor, DEFAULT_SCHEDULE, type Schedule } from './PhoneNode'

interface OrgPhoneNumber {
  id: string
  phoneNumber: string
  assistantId?: string
  assignedTo?: string
  status: string
  provider?: string
}

interface AssistantSummary {
  id: string
  name: string
}

interface AgentDetailPanelProps {
  assistant: Assistant | null
  open: boolean
  onClose: () => void
  onSave: (updated: Assistant) => void
}

/**
 * Resolve {{variables}} in a message for preview display.
 * Returns React elements with dynamic values styled as pills so users
 * know the value changes at call time.
 */
function renderMessagePreview(message: string, context: { agentName?: string; firmName?: string }): React.ReactNode {
  if (!message) return null

  // Get current time-of-day for preview
  const hour = new Date().getHours()
  const timeOfDay = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening'

  const variableMap: Record<string, { value: string; label: string }> = {
    timeOfDay: { value: timeOfDay, label: 'changes by time of day' },
    firstName: { value: 'John', label: 'caller\'s first name' },
    currentDate: { value: new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }), label: 'current date' },
    currentTimeFormatted: { value: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }), label: 'current time' },
    isBusinessHours: { value: (hour >= 9 && hour < 17) ? 'open' : 'closed', label: 'open or closed' },
    firmName: { value: context.firmName || 'your firm', label: 'firm name' },
    agentName: { value: context.agentName || 'Alex', label: 'agent name' },
    firmTimezone: { value: 'America/Los_Angeles', label: 'firm timezone' },
    businessHours: { value: 'Monday through Friday, 9am to 5pm', label: 'business hours' },
    phone: { value: '+1 555 123 4567', label: 'lead phone' },
    leadEmail: { value: 'john@example.com', label: 'lead email' },
    appointmentDate: { value: 'Thursday, May 7', label: 'appointment date' },
    appointmentTime: { value: '2:00 PM', label: 'appointment time' },
    appointmentTimezone: { value: 'Pacific', label: 'appointment timezone' },
  }

  // Split on {{variable}} patterns and interleave text with styled pills
  const parts = message.split(/(\{\{\w+\}\})/)
  return parts.map((part, i) => {
    const match = part.match(/^\{\{(\w+)\}\}$/)
    if (match) {
      const key = match[1]
      const resolved = variableMap[key]
      if (resolved) {
        return (
          <TooltipProvider key={i}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-primary/10 text-primary text-xs font-medium cursor-help border border-primary/20 border-dashed">
                  {resolved.value}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                <span className="font-mono text-[10px] text-muted-foreground">{`{{${key}}}`}</span>
                <span className="mx-1">—</span>
                {resolved.label}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )
      }
      // Unknown variable — show raw
      return <span key={i} className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 text-xs font-mono">{part}</span>
    }
    return <span key={i}>{part}</span>
  })
}

export default function AgentDetailPanel({ assistant, open, onClose, onSave }: AgentDetailPanelProps) {
  const { toast } = useToast()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)

  // Form state
  const [name, setName] = useState('')
  const [firstMessage, setFirstMessage] = useState('')
  const [outboundOpening, setOutboundOpening] = useState('')

  // Agent identity state (from org voice config)
  const [agentDisplayName, setAgentDisplayName] = useState('')
  const [greetingName, setGreetingName] = useState('')
  // Optional speakable override — what callers should hear when the AI says
  // the firm name. Falls back to auto-shortened greetingName when blank.
  const [speakableFirmName, setSpeakableFirmName] = useState('')
  const [tonePreset, setTonePreset] = useState<'warm' | 'balanced' | 'formal'>('balanced')

  // Live transfer state
  const [liveTransferEnabled, setLiveTransferEnabled] = useState(false)
  const [transferNumber, setTransferNumber] = useState('')
  const [transferNumberError, setTransferNumberError] = useState(false)
  const [transferInstructions, setTransferInstructions] = useState('')
  // AMD on transfer — Telnyx native voicemail detection on the transferred-to leg.
  // When the recipient's voicemail picks up, Telnyx either stops and returns to
  // the caller (default) or leaves a message and hangs up.
  const [amdOnTransferEnabled, setAmdOnTransferEnabled] = useState(false)
  const [amdOnTransferAction, setAmdOnTransferAction] = useState<'stop_and_return' | 'leave_message'>('stop_and_return')
  // Warm (whisper) transfer — when enabled, the AI speaks a brief to the
  // attorney BEFORE bridging the caller in. Telnyx native; maps to
  // warm_transfer_instructions on the assistant's transfer tool.
  const [warmTransferEnabled, setWarmTransferEnabled] = useState(false)
  const DEFAULT_WARM_TRANSFER_INSTRUCTIONS =
    'Briefly tell the attorney in one sentence: the caller\'s name, what kind of case it is, and the most important fact you learned. Speak naturally, like you would brief a colleague. Keep it to 25 words or fewer.'
  const [warmTransferInstructions, setWarmTransferInstructions] = useState(DEFAULT_WARM_TRANSFER_INSTRUCTIONS)
  // Org-level policy: how aggressively the AI should hand off vs. complete intake.
  // Drives the {requiredFieldsBlock}/{endOfCallRequirement} placeholders + the
  // transfer addendum's field list. Default 'fullIntake' matches legacy behavior.
  const [transferStyle, setTransferStyle] = useState<'eager' | 'fullIntake'>('fullIntake')

  // Appointment booking state. Calendar source is dynamic on the backend:
  // connected calendar when connected, otherwise the built-in Legal Lady calendar.
  const BOOKING_REQUIRED_FIELD_OPTIONS = ['name', 'phone', 'email', 'legal matter'] as const
  const BOOKING_POLICY_OPTIONS = [
    { value: 'book_directly', label: 'Book directly', description: 'Check availability, create the booking, and confirm it during the call.' },
    { value: 'offer_times_only', label: 'Offer times only', description: 'Suggest available times and collect a preference for staff confirmation.' },
    { value: 'collect_callback', label: 'Collect callback', description: 'Collect details and preferred windows without offering slots.' },
  ] as const
  const [appointmentBookingEnabled, setAppointmentBookingEnabled] = useState(false)
  const [consultationLengthMinutes, setConsultationLengthMinutes] = useState(30)
  const [bookingPolicy, setBookingPolicy] = useState<'book_directly' | 'offer_times_only' | 'collect_callback'>('book_directly')
  const [bookingInstructions, setBookingInstructions] = useState('')
  const [bookingFallbackBehavior, setBookingFallbackBehavior] = useState('')
  const [bookingRequiredFields, setBookingRequiredFields] = useState<string[]>([...BOOKING_REQUIRED_FIELD_OPTIONS])

  // SMS messaging state (Telnyx only)
  const [smsEnabled, setSmsEnabled] = useState(false)

  // Multilingual state (Telnyx only)
  const [multilingualEnabled, setMultilingualEnabled] = useState(false)

  // Webhook tools state
  const [webhookTools, setWebhookTools] = useState<Array<{
    name: string
    description: string
    webhookUrl: string
    enabled: boolean
    parameters: Array<{ name: string; type: 'string' | 'number' | 'boolean'; description: string; required: boolean }>
  }>>([])
  const [webhookToolsExpanded, setWebhookToolsExpanded] = useState(false)

  // Phone number state
  const [availablePhones, setAvailablePhones] = useState<OrgPhoneNumber[]>([])
  const [allAssistants, setAllAssistants] = useState<AssistantSummary[]>([])
  const [selectedPhoneId, setSelectedPhoneId] = useState<string>('none')
  const [originalPhoneId, setOriginalPhoneId] = useState<string>('none')

  // Schedule state. Each assigned phone has its own inbound + outbound
  // schedule on phone_numbers. We surface the relevant one based on the
  // assistant's direction so admins don't need to navigate to the FlowCanvas
  // to manage business hours.
  const isOutboundAgent = assistant?.assistantType === 'outbound'
  const [schedulePhoneId, setSchedulePhoneId] = useState<string | null>(null)
  const [schedule, setSchedule] = useState<Schedule | null>(null)
  const [scheduleDraft, setScheduleDraft] = useState<Schedule | null>(null)
  const [scheduleEditing, setScheduleEditing] = useState(false)
  const [savingSchedule, setSavingSchedule] = useState(false)
  const [loadingSchedule, setLoadingSchedule] = useState(false)
  const [reassignConfirm, setReassignConfirm] = useState<{
    open: boolean
    phoneId: string
    currentAgentName: string
  }>({ open: false, phoneId: '', currentAgentName: '' })

  // Connected forms state
  const [webhooks, setWebhooks] = useState<Array<{
    id: string
    name: string
    status: string
    callCount: number
    connected: boolean
    hasPhoneMapping: boolean
    fieldMappings: Record<string, string>
  }>>([])
  const [webhooksLoading, setWebhooksLoading] = useState(false)
  const [readiness, setReadiness] = useState<{
    hasActivePhone: boolean
    hasVoiceAgent: boolean
  }>({ hasActivePhone: true, hasVoiceAgent: true })

  // Connected calendar integration state (used to show Calendar Availability tool)
  const [connectedCalendar, setConnectedCalendar] = useState(false)

  // Knowledge base state
  const [kbDocuments, setKbDocuments] = useState<Array<{
    documentId: string
    name: string
    uploadedAt?: string
  }>>([])
  const [kbLoading, setKbLoading] = useState(false)
  const [kbExpanded, setKbExpanded] = useState(false)
  const [kbUploading, setKbUploading] = useState(false)
  const [kbTextName, setKbTextName] = useState('')
  const [kbTextContent, setKbTextContent] = useState('')
  const [kbUrlName, setKbUrlName] = useState('')
  const [kbUrl, setKbUrl] = useState('')

  // Load assistant data when opened
  useEffect(() => {
    if (assistant) {
      setName(assistant.name || '')
      setFirstMessage(assistant.firstMessage || '')

      // Initialize live transfer from toolsConfig
      const transferTool = assistant.toolsConfig?.find(t => t.type === 'transferCall')
      setLiveTransferEnabled(transferTool?.enabled || false)
      setTransferNumber(formatPhoneInput(transferTool?.config?.destinationNumber || ''))
      setTransferInstructions(transferTool?.config?.transferInstructions || '')
      setTransferNumberError(false)
      // AMD on transfer config (defaults: off, action stop_and_return)
      const amdCfg: any = transferTool?.config?.amdOnTransfer
      setAmdOnTransferEnabled(amdCfg?.enabled === true)
      setAmdOnTransferAction(amdCfg?.action === 'leave_message' ? 'leave_message' : 'stop_and_return')
      // Warm transfer config (defaults: off, default instructions)
      const warmCfg: any = transferTool?.config?.warmTransfer
      setWarmTransferEnabled(warmCfg?.enabled === true)
      setWarmTransferInstructions(
        typeof warmCfg?.instructions === 'string' && warmCfg.instructions.trim()
          ? warmCfg.instructions
          : DEFAULT_WARM_TRANSFER_INSTRUCTIONS
      )

      // Initialize appointment booking from toolsConfig. Source stays "auto":
      // backend uses connected calendar when connected, Legal Lady Calendar otherwise.
      const bookingTool = assistant.toolsConfig?.find(t => t.type === 'calendarBooking')
      setAppointmentBookingEnabled(bookingTool?.enabled ?? false)
      setConsultationLengthMinutes(Number(bookingTool?.config?.consultationLengthMinutes) || 30)
      setBookingPolicy(
        bookingTool?.config?.bookingPolicy === 'offer_times_only' || bookingTool?.config?.bookingPolicy === 'collect_callback'
          ? bookingTool.config.bookingPolicy
          : 'book_directly'
      )
      setBookingInstructions(bookingTool?.config?.bookingInstructions || '')
      setBookingFallbackBehavior(bookingTool?.config?.fallbackBehavior || '')
      const savedRequired = Array.isArray(bookingTool?.config?.requiredFields)
        ? bookingTool?.config?.requiredFields.filter(Boolean) as string[]
        : []
      setBookingRequiredFields(savedRequired.length > 0 ? savedRequired : [...BOOKING_REQUIRED_FIELD_OPTIONS])

      // Initialize SMS messaging from toolsConfig (Telnyx only)
      const sendMsgTool = assistant.toolsConfig?.find(t => t.type === 'sendMessage')
      setSmsEnabled(sendMsgTool?.enabled ?? false)

      // Initialize multilingual from toolsConfig (saved setting is source of truth)
      const voiceSettingsTool = assistant.toolsConfig?.find((t: any) => t.type === 'voiceSettings')
      if (voiceSettingsTool) {
        // User has explicitly saved this setting — use it directly
        setMultilingualEnabled(voiceSettingsTool.config?.multilingual === true)
      } else {
        // No saved setting yet — check if wizard set additional languages in the prompt
        const hasLanguageInPrompt = assistant.systemPrompt?.includes('speaks Spanish') || false
        setMultilingualEnabled(hasLanguageInPrompt)
      }

      // Initialize webhook tools from toolsConfig
      const wTools = (assistant.toolsConfig || [])
        .filter(t => t.type === 'webhook')
        .map(t => ({
          name: t.config?.name || '',
          description: t.config?.description || '',
          webhookUrl: t.config?.webhookUrl || '',
          enabled: t.enabled,
          parameters: (t.config?.webhookParameters || []).map(p => ({
            ...p,
            type: p.type as 'string' | 'number' | 'boolean',
          })),
        }))
      setWebhookTools(wTools)
    }
  }, [assistant])

  // Load voice config (agent identity + outbound opening)
  useEffect(() => {
    if (!open || !assistant) return
    api.get('/api/voice/config').then(res => {
      if (res.data.success) {
        const config = res.data.config || {}
        setAgentDisplayName(config.agentName || '')
        // Single firm-name field. Prefer the explicit speakable override; fall
        // back to legacy greetingName for orgs that haven't migrated yet.
        const firmName = config.speakableFirmName || config.greetingName || ''
        setGreetingName(firmName)
        setSpeakableFirmName(firmName)
        setTonePreset(config.tonePreset || 'balanced')
        // Default missing/'custom' policy to 'fullIntake' so the radio always
        // has a definite selected option (matches what the bake actually emits).
        const savedStyle = config.policy?.transferStyle
        setTransferStyle(savedStyle === 'eager' ? 'eager' : 'fullIntake')
        if (assistant.assistantType === 'outbound') {
          const saved = config.outboundOpening?.trim()
          const defaultGreeting = `Hi, this is ${config.agentName || 'your AI assistant'}. We received your inquiry and I wanted to follow up. Is now an okay time?`
          setOutboundOpening(saved && saved.length > 10 ? saved : defaultGreeting)
        }
      }
    }).catch(() => {})
  }, [open, assistant?.id])

  // Load phone numbers when panel opens
  useEffect(() => {
    if (open) {
      loadPhoneNumbers()
    }
  }, [open])

  // Load connected webhooks (outbound only) and KB docs when panel opens with an assistant
  useEffect(() => {
    if (open && assistant) {
      if (assistant.assistantType === 'outbound') {
        loadWebhooks(assistant.id)
      }
      loadKbDocuments(assistant.id)
      loadConnectedCalendarStatus()
    }
  }, [open, assistant?.id])

  const loadWebhooks = async (assistantId: string) => {
    setWebhooksLoading(true)
    try {
      const res = await api.get(`/api/voice/assistants/${assistantId}/webhooks`)
      if (res.data.success) {
        setWebhooks(res.data.webhooks)
        if (res.data.readiness) {
          setReadiness({
            hasActivePhone: res.data.readiness.hasActivePhone,
            hasVoiceAgent: res.data.readiness.hasVoiceAgent ?? true,
          })
        }
      }
    } catch (error) {
      console.error('Failed to load webhooks:', error)
    } finally {
      setWebhooksLoading(false)
    }
  }

  const loadKbDocuments = async (assistantId: string) => {
    setKbLoading(true)
    try {
      const res = await api.get(`/api/voice/assistants/${assistantId}/knowledge-base`)
      if (res.data.success) {
        setKbDocuments(res.data.documents || [])
      }
    } catch (error) {
      console.error('Failed to load KB documents:', error)
    } finally {
      setKbLoading(false)
    }
  }

  const loadConnectedCalendarStatus = async () => {
    try {
      const res = await api.get<{ connected: boolean }>('/api/integrations/calendar/status')
      setConnectedCalendar(!!res.data.connected)
    } catch {
      setConnectedCalendar(false)
    }
  }

  const handleKbAutoImport = async () => {
    if (!assistant) return
    setKbUploading(true)
    try {
      await api.post(`/api/voice/assistants/${assistant.id}/knowledge-base`, {
        type: 'auto',
        name: 'Firm Website Content',
      })
      toast({
        title: 'Knowledge base updated',
        description: 'Website content has been uploaded to the agent\'s knowledge base.',
      })
      loadKbDocuments(assistant.id)
    } catch (error: any) {
      toast({
        title: 'Upload failed',
        description: error.response?.data?.error || 'Failed to upload knowledge base content.',
        variant: 'destructive',
      })
    } finally {
      setKbUploading(false)
    }
  }

  const handleKbTextUpload = async () => {
    if (!assistant || !kbTextContent.trim()) return
    setKbUploading(true)
    try {
      await api.post(`/api/voice/assistants/${assistant.id}/knowledge-base`, {
        type: 'text',
        name: kbTextName.trim() || 'Custom Content',
        content: kbTextContent.trim(),
      })
      toast({
        title: 'Knowledge base updated',
        description: 'Text content has been uploaded to the agent\'s knowledge base.',
      })
      setKbTextName('')
      setKbTextContent('')
      loadKbDocuments(assistant.id)
    } catch (error: any) {
      toast({
        title: 'Upload failed',
        description: error.response?.data?.error || 'Failed to upload text content.',
        variant: 'destructive',
      })
    } finally {
      setKbUploading(false)
    }
  }

  const handleKbUrlUpload = async () => {
    if (!assistant || !kbUrl.trim()) return
    setKbUploading(true)
    try {
      await api.post(`/api/voice/assistants/${assistant.id}/knowledge-base`, {
        type: 'url',
        name: kbUrlName.trim() || kbUrl.trim(),
        url: kbUrl.trim(),
      })
      toast({
        title: 'Knowledge base updated',
        description: 'URL content has been added to the agent\'s knowledge base.',
      })
      setKbUrlName('')
      setKbUrl('')
      loadKbDocuments(assistant.id)
    } catch (error: any) {
      toast({
        title: 'Upload failed',
        description: error.response?.data?.error || 'Failed to add URL content.',
        variant: 'destructive',
      })
    } finally {
      setKbUploading(false)
    }
  }

  const handleKbDelete = async (docId: string) => {
    if (!assistant) return
    try {
      await api.delete(`/api/voice/assistants/${assistant.id}/knowledge-base/${docId}`)
      setKbDocuments(prev => prev.filter(d => d.documentId !== docId))
      toast({
        title: 'Document removed',
        description: 'Document has been removed from the knowledge base.',
      })
    } catch (error: any) {
      toast({
        title: 'Delete failed',
        description: error.response?.data?.error || 'Failed to remove document.',
        variant: 'destructive',
      })
    }
  }

  const [togglingWebhook, setTogglingWebhook] = useState(false)

  const handleToggleWebhook = async (webhookId: string, connect: boolean) => {
    if (!assistant || togglingWebhook) return
    setTogglingWebhook(true)

    // Warn on missing readiness when connecting
    if (connect) {
      const warnings: string[] = []
      if (!readiness.hasActivePhone) warnings.push('no active phone number')
      if (!readiness.hasVoiceAgent) warnings.push('voice not configured')
      const webhook = webhooks.find(w => w.id === webhookId)
      if (webhook && !webhook.hasPhoneMapping) warnings.push('no phone field mapping on this form')

      if (warnings.length > 0) {
        toast({
          title: 'Outbound calls may not work',
          description: `This form is missing: ${warnings.join(', ')}. Connecting anyway.`,
        })
      }
    }

    const updated = webhooks.map(w =>
      w.id === webhookId ? { ...w, connected: connect } : w
    )
    setWebhooks(updated)

    const connectedIds = updated.filter(w => w.connected).map(w => w.id)
    try {
      await api.put(`/api/voice/assistants/${assistant.id}/webhooks`, {
        webhookIds: connectedIds
      })
    } catch (error: any) {
      // Revert on failure
      setWebhooks(webhooks)
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to update form connection',
        variant: 'destructive'
      })
    } finally {
      setTogglingWebhook(false)
    }
  }

  const loadAiMode = async (phoneId: string) => {
    setLoadingSchedule(true)
    try {
      const res = await api.get(`/api/voice/numbers/${phoneId}/ai-mode`)
      if (res.data?.success && res.data.aiMode) {
        const direction = isOutboundAgent ? 'outboundSchedule' : 'inboundSchedule'
        const sched: Schedule | null = res.data.aiMode[direction] || null
        setSchedule(sched)
        setScheduleDraft(sched ? { ...sched, hours: { ...sched.hours } } : null)
      }
    } catch (err) {
      console.warn('Failed to load AI mode for phone:', err)
    } finally {
      setLoadingSchedule(false)
    }
  }

  const handleStartScheduleEdit = () => {
    setScheduleDraft(schedule ? { ...schedule, hours: { ...schedule.hours } } : { ...DEFAULT_SCHEDULE, hours: { ...DEFAULT_SCHEDULE.hours } })
    setScheduleEditing(true)
  }

  const handleClearSchedule = async () => {
    if (!schedulePhoneId) return
    setSavingSchedule(true)
    try {
      const direction = isOutboundAgent ? 'outboundSchedule' : 'inboundSchedule'
      await api.put(`/api/voice/numbers/${schedulePhoneId}/ai-mode`, { [direction]: null })
      setSchedule(null)
      setScheduleDraft(null)
      setScheduleEditing(false)
      toast({ title: 'Schedule cleared', description: 'Agent will run continuously when enabled.' })
    } catch (err: any) {
      toast({
        title: 'Failed to clear schedule',
        description: err?.response?.data?.message || 'Try again in a moment.',
        variant: 'destructive',
      })
    } finally {
      setSavingSchedule(false)
    }
  }

  const handleSaveSchedule = async () => {
    if (!schedulePhoneId || !scheduleDraft) return
    setSavingSchedule(true)
    try {
      const direction = isOutboundAgent ? 'outboundSchedule' : 'inboundSchedule'
      await api.put(`/api/voice/numbers/${schedulePhoneId}/ai-mode`, { [direction]: scheduleDraft })
      setSchedule(scheduleDraft)
      setScheduleEditing(false)
      toast({ title: 'Schedule saved', description: 'Agent will follow the new hours on the next call.' })
    } catch (err: any) {
      toast({
        title: 'Failed to save schedule',
        description: err?.response?.data?.message || 'Try again in a moment.',
        variant: 'destructive',
      })
    } finally {
      setSavingSchedule(false)
    }
  }

  const loadPhoneNumbers = async () => {
    try {
      const [numbersRes, assistantsRes] = await Promise.all([
        api.get('/api/voice/numbers'),
        api.get('/api/voice/assistants')
      ])

      if (numbersRes.data.success) {
        setAvailablePhones(numbersRes.data.phoneNumbers)

        // Find which phone is currently assigned to this assistant. Inbound
        // agents bind via assistantId; outbound bind via outboundAssistantId.
        const assignedPhone = numbersRes.data.phoneNumbers.find(
          (p: OrgPhoneNumber) =>
            (isOutboundAgent ? (p as any).outboundAssistantId === assistant?.id : p.assistantId === assistant?.id)
        )
        const phoneId = assignedPhone?.id || 'none'
        setSelectedPhoneId(phoneId)
        setOriginalPhoneId(phoneId)

        // Pull the AI-mode payload for the assigned phone so we can render
        // the schedule editor inline. Skipped when no phone is bound.
        if (assignedPhone?.id) {
          setSchedulePhoneId(assignedPhone.id)
          loadAiMode(assignedPhone.id)
        } else {
          setSchedulePhoneId(null)
          setSchedule(null)
          setScheduleDraft(null)
        }
      }

      if (assistantsRes.data.success) {
        setAllAssistants(assistantsRes.data.assistants.map((a: any) => ({
          id: a.id,
          name: a.name
        })))
      }
    } catch (error) {
      console.error('Failed to load phone numbers:', error)
    }
  }

  const handlePhoneChange = (phoneId: string) => {
    if (phoneId === 'none') {
      setSelectedPhoneId('none')
      return
    }

    const phone = availablePhones.find(p => p.id === phoneId)
    if (phone?.assistantId && phone.assistantId !== assistant?.id) {
      // This phone belongs to another agent — confirm reassignment
      const ownerAgent = allAssistants.find(a => a.id === phone.assistantId)
      setReassignConfirm({
        open: true,
        phoneId,
        currentAgentName: ownerAgent?.name || 'another agent'
      })
    } else {
      setSelectedPhoneId(phoneId)
    }
  }

  const confirmReassign = () => {
    setSelectedPhoneId(reassignConfirm.phoneId)
    setReassignConfirm({ open: false, phoneId: '', currentAgentName: '' })
  }

  const handleSave = async () => {
    if (!assistant) return

    // Validate transfer number when enabled
    if (liveTransferEnabled) {
      const digits = transferNumber.trim().replace(/[^\d]/g, '')
      if (!transferNumber.trim() || digits.length < 10) {
        setTransferNumberError(true)
        toast({
          title: 'Validation Error',
          description: 'Please enter a valid phone number (at least 10 digits) for live transfer.',
          variant: 'destructive'
        })
        return
      }
    }
    setTransferNumberError(false)

    setLoading(true)
    try {
      // Build toolsConfig array
      const toolsConfig: Array<{ type: string; enabled: boolean; config?: Record<string, any> }> = [{
        type: 'transferCall',
        enabled: liveTransferEnabled,
        config: {
          destinationNumber: (() => {
            let num = transferNumber.trim().replace(/[^\d+]/g, '')
            if (num && !num.startsWith('+')) num = '+1' + num.replace(/^1/, '')
            return num || undefined
          })(),
          message: 'Please hold while I connect you with one of our attorneys.',
          transferInstructions: transferInstructions.trim() || 'Collect the caller\'s full name and phone number before transferring.',
          amdOnTransfer: amdOnTransferEnabled
            ? { enabled: true, action: amdOnTransferAction }
            : { enabled: false },
          warmTransfer: warmTransferEnabled
            ? { enabled: true, instructions: warmTransferInstructions.trim() || DEFAULT_WARM_TRANSFER_INSTRUCTIONS }
            : { enabled: false },
        }
      }]

      toolsConfig.push({
        type: 'calendarBooking',
        enabled: appointmentBookingEnabled,
        config: {
          calendarSource: 'auto',
          consultationLengthMinutes,
          bookingPolicy,
          bookingInstructions: bookingInstructions.trim(),
          requiredFields: bookingRequiredFields,
          fallbackBehavior: bookingFallbackBehavior.trim(),
        }
      })

      // Add sendMessage + voiceSettings tools for Telnyx agents
      if (assistant.provider === 'telnyx') {
        toolsConfig.push({
          type: 'sendMessage',
          enabled: smsEnabled,
          config: {}
        })
        toolsConfig.push({
          type: 'voiceSettings',
          enabled: true,
          config: { multilingual: multilingualEnabled }
        })
      }

      // Add webhook tools
      for (const wt of webhookTools) {
        if (wt.name && wt.webhookUrl) {
          toolsConfig.push({
            type: 'webhook',
            enabled: wt.enabled,
            config: {
              name: wt.name,
              description: wt.description,
              webhookUrl: wt.webhookUrl,
              webhookParameters: wt.parameters,
            }
          })
        }
      }

      // Greeting is always live-derived from AI Name + Firm Name. No
      // freeform editor in the dialog — the firm name field IS the way
      // users control the greeting. {{timeOfDay}} stays as a runtime
      // variable; Telnyx resolves it at call time.
      const resolvedFirmNameForSave = resolveFirmShortName(speakableFirmName, speakableFirmName) || 'your firm'
      const greetingToSave = buildLiveGreeting({
        agentName: agentDisplayName,
        firmName: resolvedFirmNameForSave,
        assistantType: assistant.assistantType === 'outbound' ? 'outbound' : 'inbound',
      })

      // Update assistant tools config + greeting
      const response = await api.put(`/api/voice/assistants/${assistant.id}`, {
        name,
        firstMessage: greetingToSave,
        toolsConfig,
      })

      if (response.data.success) {
        // Save agent identity + outbound opening to org voice config
        // This triggers prompt + greeting regeneration on the server
        const voiceConfigUpdate: Record<string, any> = {}
        if (agentDisplayName) voiceConfigUpdate.agentName = agentDisplayName
        // Single source of truth: the user-edited "Firm Name" field updates
        // BOTH speakableFirmName (the spoken shortname) AND greetingName (the
        // prompt-context firm name). Always send both, including empty string,
        // so users can clear and fall back to auto-derivation from org name.
        voiceConfigUpdate.speakableFirmName = speakableFirmName
        voiceConfigUpdate.greetingName = speakableFirmName
        if (tonePreset) voiceConfigUpdate.tonePreset = tonePreset
        if (assistant.assistantType === 'outbound') {
          voiceConfigUpdate.outboundOpening = outboundOpening.trim()
        }
        // Org-level prompt policy. Always send so toggling between presets
        // takes effect on the next bake.
        voiceConfigUpdate.policy = { transferStyle }
        if (Object.keys(voiceConfigUpdate).length > 0) {
          await api.put('/api/voice/config', voiceConfigUpdate)
        }

        // Handle phone number assignment changes
        if (selectedPhoneId !== originalPhoneId) {
          // Unassign the old phone if there was one
          if (originalPhoneId !== 'none') {
            await api.post(`/api/voice/numbers/${originalPhoneId}/unassign-assistant`)
          }
          // Assign the new phone if one was selected
          if (selectedPhoneId !== 'none') {
            await api.post(`/api/voice/numbers/${selectedPhoneId}/assign-assistant`, {
              assistantId: assistant.id
            })
          }
        }

        toast({
          title: 'Agent updated',
          description: 'Your AI agent has been updated successfully.'
        })

        onSave({
          ...assistant,
          name,
          firstMessage,
          toolsConfig,
        })
        onClose()
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to update agent',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  const getMappingIcon = (key: string) => {
    if (key === 'lead.phone') return <Phone className="h-3 w-3" />
    if (key === 'lead.name') return <User className="h-3 w-3" />
    if (key === 'lead.email') return <Mail className="h-3 w-3" />
    if (key === 'lead.message') return <MessageSquare className="h-3 w-3" />
    if (key.startsWith('context.')) return <Briefcase className="h-3 w-3" />
    return null
  }

  const cleanMappingKey = (key: string) => key.replace(/^(lead\.|context\.)/, '')

  const stats = assistant?.stats || { callsThisWeek: 0, avgDurationSeconds: 0 }

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${minutes}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="mb-6">
          <SheetTitle className="flex items-center gap-2">
            Edit AI Agent
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-6">
          <Tabs defaultValue="identity" className="w-full">
            <TabsList className="grid grid-cols-5 w-full">
              <TabsTrigger value="identity" className="text-xs sm:text-sm">Identity</TabsTrigger>
              <TabsTrigger value="calls" className="text-xs sm:text-sm">Calls</TabsTrigger>
              <TabsTrigger value="tools" className="text-xs sm:text-sm">Tools</TabsTrigger>
              <TabsTrigger value="knowledge" className="text-xs sm:text-sm">KB</TabsTrigger>
              <TabsTrigger value="stats" className="text-xs sm:text-sm">Stats</TabsTrigger>
            </TabsList>

            <TabsContent value="identity" className="space-y-6 mt-4">
          {/* Agent Identity Card */}
          <Card className="card-inset">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <User className="h-4 w-4" />
                Agent Identity
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="agentDisplayName" className="text-xs">AI Name</Label>
                  <Input
                    id="agentDisplayName"
                    value={agentDisplayName}
                    onChange={(e) => setAgentDisplayName(e.target.value)}
                    placeholder="e.g., Molly"
                    className="h-9"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="speakableFirmName" className="text-xs">Firm Name</Label>
                  <Input
                    id="speakableFirmName"
                    value={speakableFirmName}
                    onChange={(e) => setSpeakableFirmName(e.target.value)}
                    placeholder="e.g., NAT Law"
                    className="h-9"
                  />
                </div>
              </div>
              {/* Helper text under the AI Name + Firm Name row */}
              <p className="text-[10px] text-muted-foreground leading-relaxed">
                For long firm names like <span className="italic">"Nelson, Alice & Thompson Law Group"</span>,
                use a short brand or initials like <span className="italic">"NAT Law"</span>.
                Leave blank to auto-derive from your org name (common suffixes
                like LLP / PC are automatically stripped).
              </p>

              {/* Greeting preview — always live-derived from AI Name + Firm
                  Name above. Read-only by design: change the inputs above,
                  the preview updates. No edit mode, no advanced toggle —
                  the firm name field IS the way you customize. */}
              {(() => {
                const resolvedFirmName = resolveFirmShortName(speakableFirmName, greetingName) || 'your firm'
                const liveGreeting = buildLiveGreeting({
                  agentName: agentDisplayName,
                  firmName: resolvedFirmName,
                  assistantType: assistant?.assistantType === 'outbound' ? 'outbound' : 'inbound',
                })

                return (
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">
                      {assistant?.assistantType === 'outbound' ? 'Opening line:' : 'Callers will hear:'}
                    </Label>
                    <div className="p-3 rounded-md bg-muted/50 border border-dashed">
                      <p className="text-sm leading-relaxed">
                        {renderMessagePreview(liveGreeting, { agentName: agentDisplayName, firmName: resolvedFirmName })}
                      </p>
                    </div>
                  </div>
                )
              })()}
            </CardContent>
          </Card>

          {/* Role (read-only) */}
          <div className="space-y-2">
            <Label>Role</Label>
            <div className="flex items-center gap-3 p-3 rounded-xl border border-border bg-muted/30">
              {assistant?.assistantType === 'outbound' ? (
                <>
                  <div className="h-9 w-9 rounded-lg bg-violet-500/15 flex items-center justify-center shrink-0">
                    <PhoneOutgoing className="h-5 w-5 text-violet-600 dark:text-violet-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Intake Specialist</p>
                    <p className="text-xs text-muted-foreground">Outbound calls</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="h-9 w-9 rounded-lg bg-brand-periwinkle/15 flex items-center justify-center shrink-0">
                    <PhoneIncoming className="h-5 w-5 text-brand-royal dark:text-brand-periwinkle" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Receptionist</p>
                    <p className="text-xs text-muted-foreground">Inbound calls</p>
                  </div>
                </>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Role determines the agent's conversation framework and cannot be changed after creation.
            </p>
          </div>

          {/* Opening Message — outbound agents only */}
          {assistant?.assistantType === 'outbound' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="outboundOpening">Opening Message</Label>
                <VariablePicker onInsert={(v) => setOutboundOpening(prev => prev + v)} />
              </div>
              <Textarea
                id="outboundOpening"
                value={outboundOpening}
                onChange={(e) => setOutboundOpening(e.target.value)}
                placeholder="Hey, is this {{firstName}}? This is Alex with Smith Law. We got your inquiry and I wanted to follow up. Do you have a couple minutes?"
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                What the agent says after the caller answers. Use {'{{firstName}}'} for the caller's name. Leave blank for the default.
              </p>
              {outboundOpening && outboundOpening.includes('{{') && (
                <div className="mt-1 p-2.5 rounded-md bg-muted/50 border border-dashed">
                  <p className="text-[10px] text-muted-foreground mb-1.5 uppercase tracking-wide font-medium">Live preview</p>
                  <p className="text-sm leading-relaxed">
                    {renderMessagePreview(outboundOpening, { agentName: agentDisplayName, firmName: greetingName })}
                  </p>
                </div>
              )}
            </div>
          )}

            </TabsContent>

            <TabsContent value="calls" className="space-y-6 mt-4">
          {/* Outbound agents don't have a phone-assignment dropdown here —
              phone↔outbound bindings live on FlowCanvas via drag-and-drop.
              Without this hint the tab rendered completely empty (audit
              2026-05-04). */}
          {assistant?.assistantType === 'outbound' && (
            <div className="rounded-lg border border-dashed bg-muted/30 p-5 text-sm space-y-2">
              <p className="font-medium text-foreground">Outbound agents are wired via the call flow</p>
              <p className="text-muted-foreground text-xs leading-relaxed">
                Drag this agent onto a phone number in the AI Agents canvas to set the caller ID it dials from. Lead-webhook triggers and form-fill auto-calls also live there.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/agents')}
                className="mt-1"
              >
                Open AI Agents canvas
              </Button>
            </div>
          )}

          {/* Phone Number Assignment — inbound agents only.
              Outbound agent ↔ phone connections are managed via the FlowCanvas drag-and-drop. */}
          {assistant?.assistantType !== 'outbound' && (
            <div className="space-y-2">
              <Label>Phone Number</Label>
              <Select value={selectedPhoneId} onValueChange={handlePhoneChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select phone number" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No phone assigned</SelectItem>
                  {availablePhones
                    .filter(p => p.status === 'active')
                    .map((phone) => {
                      const isOwnPhone = phone.assistantId === assistant?.id
                      const ownerAgent = phone.assistantId && !isOwnPhone
                        ? allAssistants.find(a => a.id === phone.assistantId)
                        : null
                      return (
                        <SelectItem key={phone.id} value={phone.id}>
                          <span className="flex items-center gap-2">
                            {formatPhoneDisplay(phone.phoneNumber)}
                            {ownerAgent ? <span className="text-muted-foreground text-xs">(currently: {ownerAgent.name})</span> : ''}
                          </span>
                        </SelectItem>
                      )
                    })}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Assign an inbound phone number to this agent.
              </p>
            </div>
          )}

          {/* Schedule editor — when a phone is assigned, lets the admin set
              business-hours-style time-windowing for this agent without
              navigating to FlowCanvas. Empty schedule means the agent runs
              continuously while AI mode is enabled. */}
          {schedulePhoneId && (
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <Label className="text-sm font-medium">Schedule</Label>
                </div>
                {!loadingSchedule && !scheduleEditing && (
                  <Button variant="outline" size="sm" onClick={handleStartScheduleEdit}>
                    {schedule ? 'Edit' : 'Add schedule'}
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {isOutboundAgent
                  ? 'Outbound calls only fire inside this window. Outside it, queued calls hold for the next active period.'
                  : 'Inbound calls only ring this agent inside this window. Outside it, calls forward to the configured fallback.'}
              </p>

              {loadingSchedule ? (
                <p className="text-xs text-muted-foreground italic">Loading schedule…</p>
              ) : scheduleEditing && scheduleDraft ? (
                <ScheduleEditor
                  schedule={scheduleDraft}
                  onChange={setScheduleDraft}
                  onSave={handleSaveSchedule}
                  onCancel={() => {
                    setScheduleEditing(false)
                    setScheduleDraft(schedule ? { ...schedule, hours: { ...schedule.hours } } : null)
                  }}
                  saving={savingSchedule}
                />
              ) : schedule ? (
                <div className="space-y-1.5 text-xs">
                  <p className="text-muted-foreground">Active days:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(schedule.hours).map(([day, h]) => (
                      <span
                        key={day}
                        className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${
                          h ? 'bg-brand-royal/10 text-brand-royal' : 'bg-muted text-muted-foreground line-through'
                        }`}
                      >
                        {day.slice(0, 3)}{h ? ` ${h.open}–${h.close}` : ''}
                      </span>
                    ))}
                  </div>
                  <p className="text-[10px] text-muted-foreground">Timezone: {schedule.timezone}</p>
                  <Button variant="ghost" size="sm" onClick={handleClearSchedule} disabled={savingSchedule} className="h-7 text-[11px]">
                    Clear schedule (always on)
                  </Button>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground italic">
                  No schedule set — agent runs continuously when AI mode is on.
                </p>
              )}
            </div>
          )}
            </TabsContent>

            <TabsContent value="tools" className="space-y-6 mt-4">
          {/* Live Transfer */}
          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <PhoneForwarded className="h-4 w-4 text-muted-foreground" />
                <Label htmlFor="live-transfer" className="text-sm font-medium cursor-pointer">
                  Live Transfer
                </Label>
              </div>
              <Switch
                id="live-transfer"
                checked={liveTransferEnabled}
                onCheckedChange={setLiveTransferEnabled}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Allow the AI to transfer callers to a real person mid-conversation.
            </p>
            {liveTransferEnabled && (
              <div className="space-y-2 pt-2 border-t">
                <Label htmlFor="transfer-number" className="text-sm">Transfer Phone Number</Label>
                <Input
                  id="transfer-number"
                  value={transferNumber}
                  onChange={(e) => {
                    const formatted = formatPhoneInput(e.target.value)
                    setTransferNumber(formatted)
                    if (transferNumberError) setTransferNumberError(false)
                  }}
                  placeholder="+1 (555) 123 4567"
                  className={transferNumberError ? 'border-red-500' : ''}
                />
                {transferNumberError && (
                  <p className="text-xs text-red-500">
                    A phone number is required when live transfer is enabled.
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  Enter a US phone number (e.g., +1 (555) 123 4567). Calls will be transferred here when a caller asks for a live person.
                </p>

                {/* Transfer behavior — org-level policy. Drives how aggressively
                    the AI hands off vs completes the intake before transferring. */}
                <div className="pt-3 space-y-2">
                  <Label className="text-sm">When does the AI transfer to an attorney?</Label>
                  <p className="text-xs text-muted-foreground">
                    Org-wide setting — applies to every agent for this firm.
                  </p>
                  <div className="space-y-2 pt-1">
                    <label className="flex items-start gap-2 cursor-pointer rounded-md border p-3 hover:bg-muted/40">
                      <input
                        type="radio"
                        name="transfer-style"
                        value="eager"
                        checked={transferStyle === 'eager'}
                        onChange={() => setTransferStyle('eager')}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium">Eager handoff</div>
                        <div className="text-xs text-muted-foreground">
                          Transfer as soon as the AI has name, phone, and the legal matter. The attorney captures email, incident date, and urgency on the live call. Best when an attorney is consistently available — eager handoff converts callers fastest.
                        </div>
                      </div>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer rounded-md border p-3 hover:bg-muted/40">
                      <input
                        type="radio"
                        name="transfer-style"
                        value="fullIntake"
                        checked={transferStyle === 'fullIntake'}
                        onChange={() => setTransferStyle('fullIntake')}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium">Full intake first</div>
                        <div className="text-xs text-muted-foreground">
                          Capture all six fields (name, phone, email, legal matter, incident date, urgency) before transferring. Best when the attorney isn't always available and you want a complete record either way.
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="pt-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="transfer-instructions" className="text-sm">Transfer Instructions</Label>
                    <VariablePicker onInsert={(v) => setTransferInstructions(prev => prev + v)} />
                  </div>
                  <Textarea
                    id="transfer-instructions"
                    value={transferInstructions}
                    onChange={(e) => setTransferInstructions(e.target.value)}
                    placeholder="e.g., Always collect the caller's name and phone number before transferring. Ask about the nature of their legal matter."
                    rows={4}
                  />
                  <p className="text-xs text-muted-foreground">
                    Tell your AI agent what to do before transferring a call.
                  </p>
                </div>

                {/* Warm (whisper) transfer — Telnyx native warm_transfer_instructions.
                    The AI speaks a brief to the attorney BEFORE bridging the caller in. */}
                <div className="pt-3 space-y-2 border-t">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="warm-transfer" className="text-sm font-medium">
                        Whisper a brief to the attorney first
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Before bridging the caller in, the AI speaks a one-sentence brief to the attorney so they know who's calling and why.
                      </p>
                    </div>
                    <Switch
                      id="warm-transfer"
                      checked={warmTransferEnabled}
                      onCheckedChange={setWarmTransferEnabled}
                    />
                  </div>
                  {warmTransferEnabled && (
                    <div className="space-y-2 pt-1">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="warm-transfer-instructions" className="text-xs">
                          What should the AI say to the attorney?
                        </Label>
                        {warmTransferInstructions !== DEFAULT_WARM_TRANSFER_INSTRUCTIONS && (
                          <button
                            type="button"
                            onClick={() => setWarmTransferInstructions(DEFAULT_WARM_TRANSFER_INSTRUCTIONS)}
                            className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
                          >
                            Reset to default
                          </button>
                        )}
                      </div>
                      <Textarea
                        id="warm-transfer-instructions"
                        value={warmTransferInstructions}
                        onChange={(e) => setWarmTransferInstructions(e.target.value)}
                        placeholder={DEFAULT_WARM_TRANSFER_INSTRUCTIONS}
                        rows={3}
                      />
                      <p className="text-[11px] text-muted-foreground leading-relaxed">
                        These are coaching instructions, not a script — the AI generates a fresh brief
                        from the live conversation each time. Caller hears nothing during the brief.
                      </p>
                    </div>
                  )}
                </div>

                {/* Voicemail detection on transfer (Telnyx native) */}
                <div className="pt-3 space-y-2 border-t">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="amd-on-transfer" className="text-sm font-medium">
                        Voicemail handling on transfer
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Detect when the attorney's voicemail picks up and choose what happens next.
                      </p>
                    </div>
                    <Switch
                      id="amd-on-transfer"
                      checked={amdOnTransferEnabled}
                      onCheckedChange={setAmdOnTransferEnabled}
                    />
                  </div>
                  {amdOnTransferEnabled && (
                    <div className="space-y-2 pt-1">
                      <label className="flex items-start gap-2 cursor-pointer rounded-md border p-3 hover:bg-muted/40">
                        <input
                          type="radio"
                          name="amd-action"
                          value="stop_and_return"
                          checked={amdOnTransferAction === 'stop_and_return'}
                          onChange={() => setAmdOnTransferAction('stop_and_return')}
                          className="mt-0.5"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium">Return to caller</div>
                          <div className="text-xs text-muted-foreground">
                            On voicemail, hang up the transfer leg and bring the caller back to the AI so it can offer a callback or take a message.
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-2 cursor-pointer rounded-md border p-3 hover:bg-muted/40">
                        <input
                          type="radio"
                          name="amd-action"
                          value="leave_message"
                          checked={amdOnTransferAction === 'leave_message'}
                          onChange={() => setAmdOnTransferAction('leave_message')}
                          className="mt-0.5"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium">Leave a message and hang up</div>
                          <div className="text-xs text-muted-foreground">
                            On voicemail, the AI leaves a brief on the attorney's mailbox and ends the call. Caller is not returned to the AI.
                          </div>
                        </div>
                      </label>
                    </div>
                  )}
                </div>

              </div>
            )}
          </div>

          {/* Appointment Booking */}
          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <Label htmlFor="appointment-booking" className="text-sm font-medium cursor-pointer">
                  Appointment Booking
                </Label>
              </div>
              <Switch
                id="appointment-booking"
                checked={appointmentBookingEnabled}
                onCheckedChange={setAppointmentBookingEnabled}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Let the AI book consultations. It checks connected calendar when connected; otherwise it uses the Legal Lady calendar.
            </p>

            {appointmentBookingEnabled && (
              <div className="space-y-3 pt-2 border-t">
                <div className="rounded-md bg-muted/40 border p-3 space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium">Active calendar</span>
                    <Badge variant={connectedCalendar ? 'default' : 'secondary'} className="text-[10px]">
                      {connectedCalendar ? 'Nylas calendar' : 'Legal Lady Calendar'}
                    </Badge>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    {connectedCalendar
                      ? 'The agent checks the connected Nylas calendar and writes confirmed bookings directly to that calendar.'
                      : 'The agent checks existing Legal Lady bookings and writes confirmed appointments to the Legal Lady calendar.'}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="booking-policy" className="text-sm">Booking policy</Label>
                  <Select
                    value={bookingPolicy}
                    onValueChange={(v) => setBookingPolicy(v as 'book_directly' | 'offer_times_only' | 'collect_callback')}
                  >
                    <SelectTrigger id="booking-policy" className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BOOKING_POLICY_OPTIONS.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground">
                    {BOOKING_POLICY_OPTIONS.find(option => option.value === bookingPolicy)?.description}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="consultation-length" className="text-sm">Consultation length</Label>
                  <Select
                    value={String(consultationLengthMinutes)}
                    onValueChange={(v) => setConsultationLengthMinutes(Number(v))}
                  >
                    <SelectTrigger id="consultation-length" className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[15, 30, 45, 60, 90].map(minutes => (
                        <SelectItem key={minutes} value={String(minutes)}>
                          {minutes} minutes
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Required before booking</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {BOOKING_REQUIRED_FIELD_OPTIONS.map(field => (
                      <label key={field} className="flex items-center gap-2 rounded-md border px-3 py-2 text-xs cursor-pointer hover:bg-muted/40">
                        <input
                          type="checkbox"
                          checked={bookingRequiredFields.includes(field)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setBookingRequiredFields(prev => Array.from(new Set([...prev, field])))
                            } else {
                              setBookingRequiredFields(prev => prev.filter(f => f !== field))
                            }
                          }}
                        />
                        <span className="capitalize">{field}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="booking-instructions" className="text-sm">Booking Instructions</Label>
                    <VariablePicker onInsert={(v) => setBookingInstructions(prev => prev + v)} />
                  </div>
                  <Textarea
                    id="booking-instructions"
                    value={bookingInstructions}
                    onChange={(e) => setBookingInstructions(e.target.value)}
                    placeholder="e.g., Do not book same-day consultations. For urgent injury matters, offer the earliest available slot."
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="booking-fallback" className="text-sm">If no time works</Label>
                    <VariablePicker onInsert={(v) => setBookingFallbackBehavior(prev => prev + v)} />
                  </div>
                  <Textarea
                    id="booking-fallback"
                    value={bookingFallbackBehavior}
                    onChange={(e) => setBookingFallbackBehavior(e.target.value)}
                    placeholder="e.g., Collect callback details and tell the caller someone will reach out with available times."
                    rows={2}
                  />
                </div>
              </div>
            )}
          </div>

          {/* SMS Messaging — hidden until SMS feature is ready */}

          {/* Multilingual Support — Telnyx only */}
          {assistant?.provider === 'telnyx' && (
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <Label htmlFor="multilingual" className="text-sm font-medium cursor-pointer">
                    Multilingual Support
                  </Label>
                </div>
                <Switch
                  id="multilingual"
                  checked={multilingualEnabled}
                  onCheckedChange={setMultilingualEnabled}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                The agent will detect and respond in Spanish, French, and 30+ other languages. Uses a higher-quality voice model — if callers speak only English, leave this off for faster, more natural-sounding responses.
              </p>
              {multilingualEnabled && (
                <div className="flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/5 p-2.5">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-700 dark:text-amber-400/90">
                    Multilingual mode uses a higher-latency voice model. Responses may feel slightly slower than English-only mode.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Custom Webhook Tools */}
          <div className="border rounded-lg p-4 space-y-3">
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setWebhookToolsExpanded(!webhookToolsExpanded)}
            >
              <div className="flex items-center gap-2">
                <Wrench className="h-4 w-4 text-muted-foreground" />
                <Label className="text-sm font-medium cursor-pointer">
                  Custom Tools {webhookTools.length > 0 && `(${webhookTools.length})`}
                </Label>
              </div>
              {webhookToolsExpanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Add webhook-based tools the AI can invoke during calls (e.g., CRM sync, booking, custom actions).
            </p>

            {webhookToolsExpanded && (
              <div className="space-y-3 pt-2">
                {webhookTools.map((tool, idx) => (
                  <div key={idx} className="border rounded-md p-3 space-y-2 bg-muted/30">
                    <div className="flex items-center justify-between">
                      <Input
                        placeholder="Tool name (e.g., book_appointment)"
                        value={tool.name}
                        onChange={(e) => {
                          const updated = [...webhookTools]
                          updated[idx] = { ...updated[idx], name: e.target.value }
                          setWebhookTools(updated)
                        }}
                        className="text-sm h-8 flex-1 mr-2"
                      />
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={tool.enabled}
                          onCheckedChange={(checked) => {
                            const updated = [...webhookTools]
                            updated[idx] = { ...updated[idx], enabled: checked }
                            setWebhookTools(updated)
                          }}
                        />
                        <button
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => setWebhookTools(webhookTools.filter((_, i) => i !== idx))}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <Input
                      placeholder="Description (when should the AI use this tool?)"
                      value={tool.description}
                      onChange={(e) => {
                        const updated = [...webhookTools]
                        updated[idx] = { ...updated[idx], description: e.target.value }
                        setWebhookTools(updated)
                      }}
                      className="text-sm h-8"
                    />
                    <div className="flex items-center gap-1">
                      <Globe className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                      <Input
                        placeholder="Webhook URL (https://...)"
                        value={tool.webhookUrl}
                        onChange={(e) => {
                          const updated = [...webhookTools]
                          updated[idx] = { ...updated[idx], webhookUrl: e.target.value }
                          setWebhookTools(updated)
                        }}
                        className="text-sm h-8"
                      />
                    </div>

                    {/* Parameters */}
                    {tool.parameters.length > 0 && (
                      <div className="space-y-2 pl-2 border-l-2 border-muted">
                        <span className="text-xs text-muted-foreground font-medium">Parameters</span>
                        {tool.parameters.map((param, pIdx) => (
                          <div
                            key={pIdx}
                            className="rounded-lg border bg-muted/30 p-2 space-y-2 sm:space-y-0 sm:p-0 sm:bg-transparent sm:border-0 sm:rounded-none sm:flex sm:items-center sm:gap-1.5"
                          >
                            <div className="flex items-center gap-1.5 sm:contents">
                              <Input
                                placeholder="Name"
                                value={param.name}
                                onChange={(e) => {
                                  const updated = [...webhookTools]
                                  const params = [...updated[idx].parameters]
                                  params[pIdx] = { ...params[pIdx], name: e.target.value }
                                  updated[idx] = { ...updated[idx], parameters: params }
                                  setWebhookTools(updated)
                                }}
                                className="text-xs h-7 flex-1 sm:flex-none sm:w-28"
                              />
                              <Select
                                value={param.type}
                                onValueChange={(value) => {
                                  const updated = [...webhookTools]
                                  const params = [...updated[idx].parameters]
                                  params[pIdx] = { ...params[pIdx], type: value as 'string' | 'number' | 'boolean' }
                                  updated[idx] = { ...updated[idx], parameters: params }
                                  setWebhookTools(updated)
                                }}
                              >
                                <SelectTrigger className="text-xs h-7 w-24 shrink-0">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="string">string</SelectItem>
                                  <SelectItem value="number">number</SelectItem>
                                  <SelectItem value="boolean">boolean</SelectItem>
                                </SelectContent>
                              </Select>
                              <button
                                className="sm:hidden h-7 w-7 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 shrink-0"
                                onClick={() => {
                                  const updated = [...webhookTools]
                                  updated[idx] = {
                                    ...updated[idx],
                                    parameters: updated[idx].parameters.filter((_, i) => i !== pIdx)
                                  }
                                  setWebhookTools(updated)
                                }}
                                aria-label="Remove parameter"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                            <Input
                              placeholder="Description"
                              value={param.description}
                              onChange={(e) => {
                                const updated = [...webhookTools]
                                const params = [...updated[idx].parameters]
                                params[pIdx] = { ...params[pIdx], description: e.target.value }
                                updated[idx] = { ...updated[idx], parameters: params }
                                setWebhookTools(updated)
                              }}
                              className="text-xs h-7 w-full sm:flex-1"
                            />
                            <button
                              className="hidden sm:inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 shrink-0"
                              onClick={() => {
                                const updated = [...webhookTools]
                                updated[idx] = {
                                  ...updated[idx],
                                  parameters: updated[idx].parameters.filter((_, i) => i !== pIdx)
                                }
                                setWebhookTools(updated)
                              }}
                              aria-label="Remove parameter"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <button
                      className="text-xs text-primary hover:underline"
                      onClick={() => {
                        const updated = [...webhookTools]
                        updated[idx] = {
                          ...updated[idx],
                          parameters: [...updated[idx].parameters, { name: '', type: 'string', description: '', required: true }]
                        }
                        setWebhookTools(updated)
                      }}
                    >
                      + Add parameter
                    </button>
                  </div>
                ))}

                <button
                  className="w-full border border-dashed rounded-md p-2 text-sm text-muted-foreground hover:text-primary hover:border-primary transition-colors"
                  onClick={() => setWebhookTools([...webhookTools, {
                    name: '',
                    description: '',
                    webhookUrl: '',
                    enabled: true,
                    parameters: [],
                  }])}
                >
                  + Add Custom Tool
                </button>
              </div>
            )}
          </div>

          {/* Connected Forms — only relevant for outbound agents */}
          {assistant && assistant.assistantType === 'outbound' && (
            <Card className="card-inset">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Link2 className="h-4 w-4" />
                  Connected Forms
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Readiness warning banner */}
                {(!readiness.hasActivePhone || !readiness.hasVoiceAgent) && !webhooksLoading && (
                  <Alert className="mb-3 rounded-xl border-amber-500/30 bg-amber-500/5">
                    <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    <AlertDescription className="text-amber-700 dark:text-amber-400/90 text-xs">
                      {!readiness.hasActivePhone && !readiness.hasVoiceAgent
                        ? 'No active phone number and voice not configured. Outbound calls will not work.'
                        : !readiness.hasActivePhone
                          ? 'No active phone number. Provision one in Phone Numbers to enable outbound calls.'
                          : 'Voice not configured. Save the agent to configure.'}
                    </AlertDescription>
                  </Alert>
                )}
                {webhooksLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                ) : webhooks.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-2">
                    No form connections yet. Create one in{' '}
                    <Link to="/form-connections" className="text-primary underline">
                      Form Connections
                    </Link>.
                  </p>
                ) : (
                  <TooltipProvider>
                    <div className="space-y-3">
                      {webhooks.map((webhook) => (
                        <div key={webhook.id} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-sm truncate">{webhook.name}</span>
                              <Badge variant={webhook.status === 'active' ? 'default' : 'secondary'} className="text-xs shrink-0">
                                {webhook.status}
                              </Badge>
                              {webhook.connected && !webhook.hasPhoneMapping && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>No phone field mapping. Outbound calls need a lead.phone mapping.</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                            <Switch
                              checked={webhook.connected}
                              onCheckedChange={(checked) => handleToggleWebhook(webhook.id, checked)}
                            />
                          </div>
                          {webhook.connected && (
                            webhook.fieldMappings && Object.keys(webhook.fieldMappings).length > 0 ? (
                              <div className="flex flex-wrap gap-2 pl-1">
                                {Object.entries(webhook.fieldMappings).map(([target, source]) => (
                                  <div key={target} className="flex items-center gap-1 text-xs text-muted-foreground">
                                    {getMappingIcon(target)}
                                    <Badge variant="outline" className="text-xs font-normal px-1.5 py-0">
                                      {cleanMappingKey(target)}
                                    </Badge>
                                    <ArrowRight className="h-3 w-3" />
                                    <code className="text-xs bg-muted px-1 rounded">
                              {typeof source === 'string' ? source : JSON.stringify(source)}
                            </code>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-muted-foreground pl-1">
                                No field mappings configured.{' '}
                                <Link to="/form-connections" className="text-primary underline">
                                  Configure mappings
                                </Link>
                              </p>
                            )
                          )}
                        </div>
                      ))}
                    </div>
                  </TooltipProvider>
                )}
              </CardContent>
            </Card>
          )}

          {/* System Tools — derived from actual agent configuration */}
          {assistant && (
            <Card className="card-inset">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Wrench className="h-4 w-4" />
                  System Tools
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="default" className="text-xs flex items-center gap-1">
                    <Shield className="h-3 w-3" />
                    Voicemail Detection
                  </Badge>
                  <Badge variant="default" className="text-xs flex items-center gap-1">
                    <PhoneOff className="h-3 w-3" />
                    End Call
                  </Badge>
                  {assistant.toolsConfig?.some(t => t.type === 'transferCall' && t.enabled) && (
                    <Badge variant="default" className="text-xs flex items-center gap-1">
                      <PhoneForwarded className="h-3 w-3" />
                      Live Transfer
                    </Badge>
                  )}
                  {assistant.toolsConfig?.some(t => t.type === 'calendarBooking' && t.enabled) && (
                    <Badge variant="default" className="text-xs flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Appointment Booking
                    </Badge>
                  )}
                  {kbDocuments.length > 0 && (
                    <Badge variant="default" className="text-xs flex items-center gap-1">
                      <Search className="h-3 w-3" />
                      Knowledge Search
                    </Badge>
                  )}
                  {assistant.assistantType !== 'outbound' && (
                    <Badge variant="secondary" className="text-xs flex items-center gap-1">
                      <User className="h-3 w-3" />
                      Lead Lookup
                    </Badge>
                  )}
                  {assistant.toolsConfig?.some(t => t.type === 'calendarBooking' && t.enabled) && (
                    <Badge variant="default" className="text-xs flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {connectedCalendar ? 'Connected Calendar' : 'Legal Lady Calendar'}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  These tools are automatically enabled on your AI agent.
                  {!connectedCalendar && ' Connect a Nylas calendar in Integrations to check the firm calendar; otherwise booking uses Legal Lady Calendar.'}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Voice Agent Prompt — platform admin only */}
            </TabsContent>

            <TabsContent value="knowledge" className="space-y-6 mt-4">
          {/* Knowledge Base */}
          {assistant && (
            <Card className="card-inset">
              <CardHeader className="pb-2 cursor-pointer" onClick={() => setKbExpanded(!kbExpanded)}>
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  Knowledge Base
                  <span className="text-xs text-muted-foreground font-normal ml-auto flex items-center gap-1">
                    {kbDocuments.length} document{kbDocuments.length !== 1 ? 's' : ''}
                    {kbExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  </span>
                </CardTitle>
              </CardHeader>
              {kbExpanded && (
                <CardContent>
                  {kbLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {kbDocuments.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-2">
                          No documents uploaded yet. Upload your website content to enable RAG-powered responses.
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {kbDocuments.map((doc) => (
                            <div key={doc.documentId} className="flex items-center justify-between p-2 bg-muted rounded-lg">
                              <div className="flex items-center gap-2 min-w-0">
                                <BookOpen className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                <span className="text-sm truncate">{doc.name}</span>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 flex-shrink-0"
                                onClick={() => handleKbDelete(doc.documentId)}
                              >
                                <Trash2 className="h-3 w-3 text-destructive" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      <Tabs defaultValue="auto" className="w-full">
                        <TabsList className="w-full grid grid-cols-3">
                          <TabsTrigger value="auto" className="text-xs">
                            <Upload className="h-3 w-3 mr-1" />
                            Auto-Import
                          </TabsTrigger>
                          <TabsTrigger value="text" className="text-xs">
                            <FileText className="h-3 w-3 mr-1" />
                            Paste Text
                          </TabsTrigger>
                          <TabsTrigger value="url" className="text-xs">
                            <Globe className="h-3 w-3 mr-1" />
                            Add URL
                          </TabsTrigger>
                        </TabsList>

                        <TabsContent value="auto" className="mt-3 space-y-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={handleKbAutoImport}
                            disabled={kbUploading}
                          >
                            {kbUploading ? (
                              <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                              <Upload className="h-4 w-4 mr-2" />
                            )}
                            Auto-Import Website Content
                          </Button>
                          <p className="text-xs text-muted-foreground text-center">
                            Imports content scraped during onboarding. If your website hasn't been scraped yet, use the Agent Builder wizard first.
                          </p>
                        </TabsContent>

                        <TabsContent value="text" className="mt-3 space-y-2">
                          <Input
                            placeholder="Document name (optional)"
                            value={kbTextName}
                            onChange={(e) => setKbTextName(e.target.value)}
                            className="text-sm"
                          />
                          <Textarea
                            placeholder="Paste your content here..."
                            value={kbTextContent}
                            onChange={(e) => setKbTextContent(e.target.value)}
                            rows={4}
                            className="text-sm"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={handleKbTextUpload}
                            disabled={kbUploading || !kbTextContent.trim()}
                          >
                            {kbUploading ? (
                              <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                              <Upload className="h-4 w-4 mr-2" />
                            )}
                            Upload Text
                          </Button>
                        </TabsContent>

                        <TabsContent value="url" className="mt-3 space-y-2">
                          <Input
                            placeholder="Document name (optional)"
                            value={kbUrlName}
                            onChange={(e) => setKbUrlName(e.target.value)}
                            className="text-sm"
                          />
                          <Input
                            placeholder="https://example.com/page"
                            value={kbUrl}
                            onChange={(e) => setKbUrl(e.target.value)}
                            className="text-sm"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={handleKbUrlUpload}
                            disabled={kbUploading || !kbUrl.trim()}
                          >
                            {kbUploading ? (
                              <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                              <Globe className="h-4 w-4 mr-2" />
                            )}
                            Add URL
                          </Button>
                        </TabsContent>
                      </Tabs>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          )}

            </TabsContent>

            <TabsContent value="stats" className="space-y-6 mt-4">
          {/* Stats Card */}
          {assistant && (
            <Card className="card-inset">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Performance</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    Calls this week
                  </span>
                  <span className="font-medium">{stats.callsThisWeek}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Avg duration
                  </span>
                  <span className="font-medium">{formatDuration(stats.avgDurationSeconds)}</span>
                </div>
                {assistant.phoneNumbers && assistant.phoneNumbers.length > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      Phone number
                    </span>
                    <span className="font-medium">{assistant.phoneNumbers[0].phoneNumber}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

            </TabsContent>
          </Tabs>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <Button variant="outline" className="flex-1" onClick={onClose}>
              Cancel
            </Button>
            <Button className="flex-1" onClick={handleSave} disabled={loading}>
              {loading ? (
                <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </div>
      </SheetContent>

      {/* Reassignment confirmation dialog */}
      <Dialog
        open={reassignConfirm.open}
        onOpenChange={(open) => !open && setReassignConfirm({ open: false, phoneId: '', currentAgentName: '' })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reassign Phone Number?</DialogTitle>
            <DialogDescription>
              This phone number is currently assigned to <strong>{reassignConfirm.currentAgentName}</strong>.
              Reassigning it will remove it from that agent and assign it to <strong>{name || 'this agent'}</strong>.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReassignConfirm({ open: false, phoneId: '', currentAgentName: '' })}
            >
              Cancel
            </Button>
            <Button onClick={confirmReassign}>
              Reassign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Sheet>
  )
}
